import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableContainer, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Button, 
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Alert,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Verified as VerifiedIcon,
  Business as BusinessIcon,
  MonetizationOn as MonetizationOnIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../config';
import { normalizeImageUrl } from '../Post';

// TabPanel コンポーネント
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ContentManagement = ({ showMessage }) => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadStatus, setCsvUploadStatus] = useState('idle');
  const [csvUploadError, setCsvUploadError] = useState(null);
  const [userFilter, setUserFilter] = useState('');

  // 企業と広告収益関連のステート
  const [corporateUsers, setCorporateUsers] = useState([]);
  const [corporateLoading, setCorporateLoading] = useState(false);
  const [revenueData, setRevenueData] = useState({
    corporate_revenue: 0,
    user_revenue: 0,
    ad_revenue: 0,
    total_revenue: 0,
    corporate_count: 0,
    total_tagged_users: 0,
    period_start: '',
    period_end: '',
    is_future: false
  });
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  
  // ユーザー一覧を取得
  useEffect(() => {
    fetchUsers();
    generateAvailableMonths();
  }, []);

  // タブ切り替え時に必要なデータをロード
  useEffect(() => {
    if (tabValue === 2) {  // 企業・収益タブが選択された時
      fetchCorporateUsers();
      fetchRevenueData();
    }
  }, [tabValue]);

  // 利用可能な月のリストを生成
  const generateAvailableMonths = () => {
    const months = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // JavaScriptの月は0始まり
    
    // 現在の月から6ヶ月前までのデータを表示
    for (let i = 0; i < 12; i++) {
      let year = currentYear;
      let month = currentMonth - i;
      
      // 1月以前は前年の月として計算
      if (month <= 0) {
        month += 12;
        year--;
      }
      
      // YYYYMM形式で保存
      const yearMonth = `${year}${month.toString().padStart(2, '0')}`;
      
      // 表示用の文字列（例: 2023年01月）
      const displayText = `${year}年${month.toString().padStart(2, '0')}月`;
      
      months.push({
        value: yearMonth,
        label: displayText
      });
    }
    
    setAvailableMonths(months);
    // デフォルトで現在の月を選択
    setSelectedMonth(months[0].value);
  };

  // 企業ユーザー一覧を取得
  const fetchCorporateUsers = async () => {
    setCorporateLoading(true);
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/stats/corporate/users`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.data.status === 'success') {
        setCorporateUsers(response.data.data.corporates || []);
      } else {
        showMessage('企業ユーザー一覧の取得に失敗しました', 'error');
        setCorporateUsers([]);
      }
    } catch (error) {
      
      showMessage('企業ユーザー一覧の取得に失敗しました', 'error');
      setCorporateUsers([]);
    } finally {
      setCorporateLoading(false);
    }
  };

  // 広告収益データを取得
  const fetchRevenueData = async (monthParam = selectedMonth) => {
    setRevenueLoading(true);
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/stats/admin/revenue`,
        {
          params: {
            yearMonth: monthParam
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.data.status === 'success') {
        setRevenueData(response.data.data);
      } else {
        showMessage('収益データの取得に失敗しました', 'error');
      }
    } catch (error) {
      
      showMessage('収益データの取得に失敗しました', 'error');
    } finally {
      setRevenueLoading(false);
    }
  };

  // 月選択時の処理
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    fetchRevenueData(newMonth);
  };
  
  // ユーザー一覧を取得
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/users`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.data && response.data.users) {
        setUsers(response.data.users);
      }
    } catch (error) {
      
      showMessage('ユーザー一覧の取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ユーザーの投稿を取得
  const fetchUserPosts = async (userId) => {
    if (!userId) return;
    
    setLoading(true);
    setSelectedPosts([]);
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/admin/posts/user/${userId}`,
        {
          params: {
            page: page + 1,
            per_page: rowsPerPage,
            search: searchQuery,
            include_deleted: true
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.data && response.data.status === 'success') {
        setPosts(response.data.data.posts);
        setTotalCount(response.data.data.pagination.total);
      } else {
        setPosts([]);
        setTotalCount(0);
      }
    } catch (error) {
      
      showMessage('投稿の取得に失敗しました', 'error');
      setPosts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ユーザー選択時の処理
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setPage(0);
    setSearchQuery('');
    fetchUserPosts(user.id);
  };

  // タブ変更時の処理
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // 「企業・収益」タブが選択されたときにデータを取得
    if (newValue === 2) {
      fetchCorporateUsers();
      fetchRevenueData();
    }
  };

  // ページ変更時の処理
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (selectedUser) {
      fetchUserPosts(selectedUser.id);
    }
  };

  // 行数変更時の処理
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (selectedUser) {
      fetchUserPosts(selectedUser.id);
    }
  };

  // 検索クエリ変更時の処理
  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // 検索実行
  const handleSearch = () => {
    setPage(0);
    if (selectedUser) {
      fetchUserPosts(selectedUser.id);
    }
  };

  // ユーザーフィルター変更時の処理
  const handleUserFilterChange = (event) => {
    setUserFilter(event.target.value);
  };

  // 投稿選択状態の切り替え
  const handleSelectPost = (postId) => {
    setSelectedPosts(prev => {
      if (prev.includes(postId)) {
        return prev.filter(id => id !== postId);
      } else {
        return [...prev, postId];
      }
    });
  };

  // すべての投稿選択状態の切り替え
  const handleSelectAllPosts = (event) => {
    if (event.target.checked) {
      setSelectedPosts(posts.map(post => post.id));
    } else {
      setSelectedPosts([]);
    }
  };

  // 一括操作ダイアログ表示
  const handleOpenBulkActionDialog = (action) => {
    if (selectedPosts.length === 0) {
      showMessage('投稿が選択されていません', 'warning');
      return;
    }
    setBulkAction(action);
    setBulkActionDialog(true);
  };

  // 一括操作ダイアログ閉じる
  const handleCloseBulkActionDialog = () => {
    setBulkActionDialog(false);
    setBulkAction('');
  };

  // 一括操作実行
  const handleExecuteBulkAction = async () => {
    if (selectedPosts.length === 0) {
      handleCloseBulkActionDialog();
      return;
    }

    setBulkActionLoading(true);
    try {
      let endpoint, data, successMessage;
      
      if (bulkAction === 'hide') {
        endpoint = `${config.API_BASE_URL}/api/admin/posts/bulk/hide`;
        data = { post_ids: selectedPosts };
        successMessage = '選択した投稿を非表示にしました';
      } else if (bulkAction === 'show') {
        endpoint = `${config.API_BASE_URL}/api/admin/posts/bulk/show`;
        data = { post_ids: selectedPosts };
        successMessage = '選択した投稿を表示状態にしました';
      } else if (bulkAction === 'delete') {
        endpoint = `${config.API_BASE_URL}/api/admin/posts/bulk/delete`;
        data = { post_ids: selectedPosts };
        successMessage = '選択した投稿を削除しました';
      } else {
        throw new Error('無効な操作です');
      }
      
      const response = await axios.post(
        endpoint,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.data && response.data.status === 'success') {
        showMessage(successMessage, 'success');
        setSelectedPosts([]);
        fetchUserPosts(selectedUser.id);
      } else {
        throw new Error(response.data.message || '操作に失敗しました');
      }
    } catch (error) {
      
      showMessage(`操作に失敗しました: ${error.message}`, 'error');
    } finally {
      setBulkActionLoading(false);
      handleCloseBulkActionDialog();
    }
  };

  // CSVダウンロード
  const handleDownloadCsv = async () => {
    if (!selectedUser) {
      showMessage('ユーザーが選択されていません', 'warning');
      return;
    }
    
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/posts/user/${selectedUser.id}/csv`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          },
          responseType: 'blob'
        }
      );
      
      // BlobからURLを作成してダウンロード
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `posts_${selectedUser.username}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();
      
      showMessage('CSVファイルをダウンロードしました', 'success');
    } catch (error) {
      
      
      // エラーの種類に応じたメッセージを表示
      if (error.response && error.response.status === 404) {
        showMessage('CSVダウンロード機能は現在実装されていません。開発中の機能です。', 'warning');
      } else if (error.response && error.response.status === 403) {
        showMessage('このアクションを実行する権限がありません', 'error');
      } else {
        showMessage('CSVダウンロードに失敗しました: ' + (error.response?.data?.message || error.message), 'error');
      }
    }
  };

  // CSVアップロードダイアログを開く
  const handleOpenCsvUploadDialog = () => {
    if (!selectedUser) {
      showMessage('ユーザーが選択されていません', 'warning');
      return;
    }
    setCsvFile(null);
    setCsvUploadStatus('idle');
    setCsvUploadError(null);
    setCsvUploadOpen(true);
  };

  // CSVアップロードダイアログを閉じる
  const handleCloseCsvUploadDialog = () => {
    setCsvUploadOpen(false);
  };

  // CSVファイル選択
  const handleFileSelect = (event) => {
    if (event.target.files.length === 0) return;
    
    const file = event.target.files[0];
    if (!file.name.endsWith('.csv')) {
      showMessage('CSVファイルを選択してください', 'error');
      return;
    }
    
    setCsvFile(file);
  };

  // CSVアップロード実行
  const handleUploadCsv = async () => {
    if (!csvFile) {
      showMessage('ファイルを選択してください', 'error');
      return;
    }
    
    if (!selectedUser) {
      showMessage('ユーザーが選択されていません', 'error');
      return;
    }
    
    setCsvUploadStatus('loading');
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('user_id', selectedUser.id);
      
      const response = await axios.post(
        `${config.API_BASE_URL}/api/posts/import/csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.data && response.data.status === 'success') {
        setCsvUploadStatus('success');
        showMessage('CSVファイルのインポートに成功しました', 'success');
        fetchUserPosts(selectedUser.id);
      } else {
        setCsvUploadStatus('error');
        setCsvUploadError(response.data.message || 'インポートに失敗しました');
      }
    } catch (error) {
      setCsvUploadStatus('error');
      
      // エラーの種類に応じたメッセージを設定
      if (error.response && error.response.status === 404) {
        setCsvUploadError('CSVアップロード機能は現在実装されていません。開発中の機能です。');
        showMessage('CSVアップロード機能は現在実装されていません。開発中の機能です。', 'warning');
      } else {
        setCsvUploadError(error.response?.data?.message || error.message || 'インポートに失敗しました');
        showMessage('CSVアップロードに失敗しました', 'error');
      }
      
      
    }
  };

  // フィルタリングされたユーザーリスト
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(userFilter.toLowerCase()) ||
    user.display_name.toLowerCase().includes(userFilter.toLowerCase())
  );

  // 投稿の削除状態を判定する関数
  const isPostDeleted = (post) => {
    // 削除状態を示す可能性のあるすべてのフィールドをチェック
    return Boolean(
      post.deleted_at || 
      post.is_deleted || 
      post.status === 'deleted' ||
      post.status === 'DELETED' ||
      post.deleted === true ||
      post.active === false
    );
  };

  // 収益CSVダウンロード関数を追加
  const downloadRevenueCsv = async () => {
    try {
      // ボタンをクリックした時点の選択月を使用
      const params = {
        yearMonth: selectedMonth
      };
      
      // ダウンロード中メッセージを表示
      showMessage('CSVファイルのダウンロードを開始します...', 'info');
      
      // APIリクエストを発行
      const response = await axios.get(
        `${config.API_BASE_URL}/api/stats/admin/revenue/csv`,
        {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          },
          responseType: 'blob' // 重要: BLOBとして受信
        }
      );
      
      // BlobからURLを作成
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // ダウンロードリンクを作成
      const link = document.createElement('a');
      link.href = url;
      
      // ファイル名を設定
      const fileName = `revenue_report_${selectedMonth || 'current'}.csv`;
      link.setAttribute('download', fileName);
      
      // リンクをクリックしてダウンロード
      document.body.appendChild(link);
      link.click();
      
      // 使用後にリンクとURLを解放
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showMessage('CSVファイルのダウンロードが完了しました', 'success');
    } catch (error) {
      
      showMessage('収益データのCSVダウンロードに失敗しました', 'error');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        コンテンツ管理
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="content management tabs">
          <Tab label="ユーザー別投稿管理" />
          <Tab label="統計" />
          <Tab label="企業・収益" />
        </Tabs>
      </Box>
      
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* ユーザー一覧 */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>ユーザー一覧</Typography>
                
                <TextField
                  variant="outlined"
                  placeholder="ユーザー名で検索..."
                  fullWidth
                  size="small"
                  value={userFilter}
                  onChange={handleUserFilterChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: userFilter && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setUserFilter('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
                  <List component="nav">
                    {loading && filteredUsers.length === 0 ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map(user => (
                        <ListItem
                          button
                          key={user.id}
                          selected={selectedUser && selectedUser.id === user.id}
                          onClick={() => handleUserSelect(user)}
                        >
                          <ListItemAvatar>
                            <Avatar src={normalizeImageUrl(user.avatar)} alt={user.username}>
                              {user.username.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={user.display_name || user.username}
                            secondary={user.username}
                          />
                          {user.verified && (
                            <Tooltip title="認証済み">
                              <VerifiedIcon color="primary" fontSize="small" />
                            </Tooltip>
                          )}
                        </ListItem>
                      ))
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="textSecondary">
                          ユーザーが見つかりません
                        </Typography>
                      </Box>
                    )}
                  </List>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* 投稿一覧 */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {selectedUser ? `${selectedUser.display_name || selectedUser.username}の投稿` : '投稿一覧'}
                </Typography>
                
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCsv}
                    disabled={!selectedUser}
                    sx={{ mr: 1 }}
                  >
                    CSVダウンロード
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={handleOpenCsvUploadDialog}
                    disabled={!selectedUser}
                  >
                    CSVアップロード
                  </Button>
                </Box>
              </Box>
              
              {selectedUser && (
                <>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <TextField
                      placeholder="投稿内容で検索..."
                      variant="outlined"
                      size="small"
                      value={searchQuery}
                      onChange={handleSearchQueryChange}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                      sx={{ mr: 1, flex: 1 }}
                    />
                    
                    <Button
                      variant="contained"
                      onClick={handleSearch}
                      startIcon={<SearchIcon />}
                    >
                      検索
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<VisibilityOffIcon />}
                      onClick={() => handleOpenBulkActionDialog('hide')}
                      disabled={selectedPosts.length === 0}
                      sx={{ mr: 1 }}
                    >
                      一括非表示
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenBulkActionDialog('show')}
                      disabled={selectedPosts.length === 0}
                      sx={{ mr: 1 }}
                    >
                      一括表示
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleOpenBulkActionDialog('delete')}
                      disabled={selectedPosts.length === 0}
                    >
                      一括削除
                    </Button>
                    
                    <Typography variant="body2" sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                      {selectedPosts.length}件選択中
                    </Typography>
                  </Box>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedPosts.length > 0 && selectedPosts.length < posts.length}
                              checked={posts.length > 0 && selectedPosts.length === posts.length}
                              onChange={handleSelectAllPosts}
                            />
                          </TableCell>
                          <TableCell>ID</TableCell>
                          <TableCell>内容</TableCell>
                          <TableCell>投稿日時</TableCell>
                          <TableCell>状態</TableCell>
                          <TableCell>いいね数</TableCell>
                          <TableCell>コメント数</TableCell>
                          <TableCell>閲覧数</TableCell>
                          <TableCell>削除日時</TableCell>
                          <TableCell>アクション</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        ) : posts.length > 0 ? (
                          posts.map(post => (
                            <TableRow 
                              key={post.id} 
                              hover
                              sx={isPostDeleted(post) ? { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: 'text.disabled' } : {}}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedPosts.includes(post.id)}
                                  onChange={() => handleSelectPost(post.id)}
                                />
                              </TableCell>
                              <TableCell>{post.id}</TableCell>
                              <TableCell>
                                <Typography 
                                  noWrap 
                                  sx={{ 
                                    maxWidth: 200,
                                    ...(isPostDeleted(post) && { 
                                      textDecoration: 'line-through',
                                      color: 'text.disabled'
                                    })
                                  }}
                                >
                                  {post.text}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {new Date(post.created_at).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {isPostDeleted(post) ? (
                                  <Chip size="small" label="削除済み" color="error" sx={{ backgroundColor: '#9e9e9e' }} />
                                ) : post.is_hidden ? (
                                  <Chip size="small" label="非表示" color="error" />
                                ) : (
                                  <Chip size="small" label="表示中" color="success" />
                                )}
                              </TableCell>
                              <TableCell>{post.like_count || 0}</TableCell>
                              <TableCell>{post.comment_count || 0}</TableCell>
                              <TableCell>{post.view_count || 0}</TableCell>
                              <TableCell>
                                {isPostDeleted(post) ? (
                                  post.deleted_at ? 
                                    new Date(post.deleted_at).toLocaleString() : 
                                    '削除済み（日時不明）'
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                <Tooltip title="詳細">
                                  <IconButton size="small" color="primary" onClick={() => window.open(`/post/${post.id}`, '_blank')}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              投稿が見つかりません
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="表示件数:"
                  />
                </>
              )}
              
              {!selectedUser && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="textSecondary">
                    ユーザーを選択してください
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6">投稿統計情報</Typography>
        <Typography variant="body1">準備中...</Typography>
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>企業・収益情報</Typography>
        
        <Box sx={{ mb: 3 }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="month-select-label">期間</InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              onChange={handleMonthChange}
              label="期間"
            >
              {availableMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {revenueData.is_future && (
            <Alert severity="info" sx={{ mt: 1 }}>
              これは将来の予測データです。実際の値とは異なる場合があります。
            </Alert>
          )}
        </Box>
        
        <Grid container spacing={3}>
          {/* 収益サマリーカード */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MonetizationOnIcon sx={{ mr: 1 }} />
                  収益サマリー
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={downloadRevenueCsv}
                    disabled={revenueLoading}
                  >
                    CSV
                  </Button>
                </Typography>
                {revenueLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">期間:</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {revenueData.period_start ? new Date(revenueData.period_start).toLocaleDateString('ja-JP') : ''}
                        ～
                        {revenueData.period_end ? new Date(revenueData.period_end).toLocaleDateString('ja-JP') : ''}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>企業ユーザー収益</TableCell>
                            <TableCell align="right">¥{revenueData.corporate_revenue.toLocaleString()}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>タグ付きユーザー収益</TableCell>
                            <TableCell align="right">¥{revenueData.user_revenue.toLocaleString()}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>広告収益</TableCell>
                            <TableCell align="right">¥{revenueData.ad_revenue.toLocaleString()}</TableCell>
                          </TableRow>
                          <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 'bold' } }}>
                            <TableCell>総収益</TableCell>
                            <TableCell align="right">¥{revenueData.total_revenue.toLocaleString()}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">企業ユーザー数:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {revenueData.corporate_count}社
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">タグ付きユーザー数:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {revenueData.total_tagged_users}人
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* 企業ユーザーリスト */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1 }} />
                  企業ユーザー一覧
                </Typography>
                
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {corporateLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : corporateUsers.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>企業名</TableCell>
                            <TableCell>ユーザー名</TableCell>
                            <TableCell>タグ数</TableCell>
                            <TableCell>広告数</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {corporateUsers.map((user) => (
                            <TableRow key={user.id} hover>
                              <TableCell>{user.shop_name || '-'}</TableCell>
                              <TableCell>{user.username}</TableCell>
                              <TableCell align="center">{user.tag_count || 0}</TableCell>
                              <TableCell align="center">{user.ad_count || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography color="textSecondary">
                        企業ユーザーが見つかりません
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* 一括操作確認ダイアログ */}
      <Dialog open={bulkActionDialog} onClose={handleCloseBulkActionDialog}>
        <DialogTitle>
          {bulkAction === 'hide' && '投稿の一括非表示'}
          {bulkAction === 'show' && '投稿の一括表示'}
          {bulkAction === 'delete' && '投稿の一括削除'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {bulkAction === 'hide' && `選択した${selectedPosts.length}件の投稿を非表示にします。よろしいですか？`}
            {bulkAction === 'show' && `選択した${selectedPosts.length}件の投稿を表示状態にします。よろしいですか？`}
            {bulkAction === 'delete' && `選択した${selectedPosts.length}件の投稿を削除します。この操作は元に戻せません。よろしいですか？`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkActionDialog}>キャンセル</Button>
          <Button
            onClick={handleExecuteBulkAction}
            color={bulkAction === 'delete' ? 'error' : 'primary'}
            disabled={bulkActionLoading}
            startIcon={bulkActionLoading ? <CircularProgress size={20} /> : null}
          >
            {bulkAction === 'hide' && '非表示にする'}
            {bulkAction === 'show' && '表示する'}
            {bulkAction === 'delete' && '削除する'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* CSVアップロードダイアログ */}
      <Dialog open={csvUploadOpen} onClose={handleCloseCsvUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>投稿CSVアップロード</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              {selectedUser && `${selectedUser.display_name || selectedUser.username}の投稿情報をCSVファイルからインポートします。`}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              CSVフォーマット説明
            </Typography>
            <Typography variant="body2" component="div">
              <Box component="p" sx={{ mt: 1 }}>CSVファイルは以下の項目を含むことができます：</Box>
              <ul>
                <li>id - 既存投稿の場合は投稿ID（新規投稿の場合は空白）</li>
                <li>text - 投稿テキスト（必須）</li>
                <li>image - 画像URL（任意）</li>
                <li>created_at - 投稿日時（YYYY-MM-DD HH:MM:SS形式、新規投稿の場合は現在時刻が設定されます）</li>
                <li>is_hidden - 非表示フラグ（1=非表示、0=表示）</li>
                <li>hashtags - ハッシュタグ（カンマ区切り）</li>
              </ul>
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<DownloadIcon />}
            >
              CSVファイル選択
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileSelect}
              />
            </Button>
            <Typography variant="body2" sx={{ ml: 2 }}>
              {csvFile ? csvFile.name : 'ファイルが選択されていません'}
            </Typography>
          </Box>
          
          {csvUploadStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {csvUploadError}
            </Alert>
          )}
          
          {csvUploadStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              CSVファイルが正常にアップロードされました。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCsvUploadDialog}>キャンセル</Button>
          <Button 
            onClick={handleUploadCsv} 
            color="primary" 
            disabled={!csvFile || csvUploadStatus === 'loading'}
            startIcon={csvUploadStatus === 'loading' ? <CircularProgress size={20} /> : null}
          >
            アップロード
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentManagement; 